import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Mail, Phone, MapPin, Send, Instagram } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        toast({
          title: t('contact.form.successTitle'),
          description: t('contact.form.successDescription'),
        });
        setFormData({ name: "", email: "", phone: "", message: "" });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to send message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="py-20 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-4xl lg:text-5xl font-serif font-bold text-[#2C3E50]">
            {t('contact.title')}
          </h2>
          <div className="h-1 w-24 bg-[#7C9885] mx-auto"></div>
          <p className="text-lg text-[#5A6B6C] max-w-2xl mx-auto mt-6">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <Card className="border-2 border-[#EDE7DC] shadow-xl">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-[#2C3E50] mb-2">
                    {t('contact.form.name')}
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="border-[#EDE7DC] focus:border-[#7C9885] focus:ring-[#7C9885]"
                    placeholder={t('contact.form.namePlaceholder')}
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#2C3E50] mb-2">
                    {t('contact.form.email')}
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="border-[#EDE7DC] focus:border-[#7C9885] focus:ring-[#7C9885]"
                    placeholder={t('contact.form.emailPlaceholder')}
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-[#2C3E50] mb-2">
                    {t('contact.form.phone')}
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="border-[#EDE7DC] focus:border-[#7C9885] focus:ring-[#7C9885]"
                    placeholder={t('contact.form.phonePlaceholder')}
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-[#2C3E50] mb-2">
                    {t('contact.form.message')}
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    className="border-[#EDE7DC] focus:border-[#7C9885] focus:ring-[#7C9885] min-h-[120px]"
                    placeholder={t('contact.form.messagePlaceholder')}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#7C9885] hover:bg-[#6A8573] text-white py-6 text-lg transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-5 h-5 mr-2" />
                  {isSubmitting ? "Sending..." : t('contact.form.submit')}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <div className="space-y-6">
            <Card className="border-2 border-[#EDE7DC] hover:border-[#7C9885] transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#7C9885]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#7C9885]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2C3E50] mb-1">{t('contact.info.email')}</h3>
                    <p className="text-[#5A6B6C]">{t('contact.info.emailValue')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-[#EDE7DC] hover:border-[#7C9885] transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#7C9885]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#7C9885]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2C3E50] mb-1">{t('contact.info.phone')}</h3>
                    <p className="text-[#5A6B6C]">{t('contact.info.phoneValue')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-[#EDE7DC] hover:border-[#7C9885] transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#7C9885]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#7C9885]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2C3E50] mb-1">{t('contact.info.sessions')}</h3>
                    <p className="text-[#5A6B6C]">{t('contact.info.sessionsValue')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-[#EDE7DC] hover:border-[#7C9885] transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#7C9885]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Instagram className="w-6 h-6 text-[#7C9885]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2C3E50] mb-1">{t('contact.info.followMe')}</h3>
                    <a
                      href="https://www.instagram.com/chabouredouan"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#7C9885] hover:text-[#6A8573] transition-colors duration-200"
                    >
                      @chabouredouan
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;